<script setup lang="ts">
import { ref, computed, watch, defineComponent, h } from 'vue'
import { Check, ChevronsUpDown } from 'lucide-vue-next'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  useCommand,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { useVModel } from '@vueuse/core'
import Spinner from '@/components/common/Spinner.vue'
import type { PropType } from 'vue'

const props = defineProps({
  modelValue: { type: [String, Number, Object, Array], default: '' },
  options: { type: Array as PropType<any[]>, default: () => [] },
  placeholder: { type: String, default: 'Select option...' },
  searchPlaceholder: { type: String, default: 'Search...' },
  emptyText: { type: String, default: 'No results found.' },
  loading: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  triggerClass: { type: String, default: '' },
  valueKey: { type: String, default: 'value' },
  labelKey: { type: String, default: 'label' },
  imageKey: { type: String, default: 'img' },
  iconKey: { type: String, default: 'icon' },
  // When true, component manages modelValue as an array of selected option values.
  multiple: { type: Boolean, default: false },
  /** When true, typing emits `search` for remote/API filtering (client filter disabled). */
  remote: { type: Boolean, default: false },
})

const emits = defineEmits(['update:modelValue', 'search'])

const modelValue = useVModel(props, 'modelValue', emits, {
  passive: true
})

const open = ref(false)

/** Bridges Command filterState.search → parent `@search` (must render inside Command). */
const CommandSearchBridge = defineComponent({
  name: 'CommandSearchBridge',
  emits: ['search'],
  setup(_, { emit }) {
    const { filterState } = useCommand()
    watch(
      () => filterState.search,
      (query) => emit('search', query ?? ''),
      { immediate: true },
    )
    return () => h('span', { class: 'hidden', 'aria-hidden': 'true' })
  },
})

// Helper to normalize options
const normalizedOptions = computed(() => {
  return props.options.map(opt => {
    if (typeof opt === 'object' && opt !== null) {
      return {
        value: opt[props.valueKey],
        label: opt[props.labelKey],
        img: opt[props.imageKey],
        icon: opt[props.iconKey],
        disabled: opt.disabled || false,
        raw: opt
      }
    }
    return { value: opt, label: opt, raw: opt, disabled: false }
  })
})

const selectedValues = computed(() => {
  if (!props.multiple) return [modelValue.value]
  return Array.isArray(modelValue.value) ? modelValue.value : (modelValue.value ? [modelValue.value] : [])
})

const selectedOption = computed(() => {
  if (props.multiple) return null
  return normalizedOptions.value.find(opt => opt.value === modelValue.value)
})

const selectedOptions = computed(() => {
  if (!props.multiple) return []
  const values = selectedValues.value
  return normalizedOptions.value.filter(opt => values.includes(opt.value))
})

const isSelected = (val: any) => {
  if (!props.multiple) return modelValue.value === val
  return selectedValues.value.includes(val)
}

const handleSelect = (val: any) => {
  if (props.multiple) {
    const current = selectedValues.value
    const exists = current.includes(val)
    const next = exists ? current.filter(v => v !== val) : [...current, val]
    modelValue.value = next
    // Keep the dropdown open to allow picking multiple values.
    return
  }

  modelValue.value = val === modelValue.value ? '' : val
  open.value = false
}
</script>

<template>
  <div class="w-full">
    <Popover v-model:open="open">
      <PopoverTrigger as-child>
        <Button
          variant="outline"
          role="combobox"
          :aria-expanded="open"
          :disabled="disabled"
          class="w-full justify-between bg-background font-normal"
          :class="triggerClass"
        >
          <div class="flex items-center truncate w-full gap-2">
            <Spinner
              v-if="loading"
              class="size-4 shrink-0"
            />
            <slot
              name="selected"
              :value="modelValue"
              :options="selectedOptions"
              :option="selectedOption"
              :placeholder="placeholder"
            >
              <span
                v-if="props.multiple ? selectedOptions.length : selectedOption"
                class="truncate flex items-center gap-2"
              >
                <img
                  v-if="props.multiple ? selectedOptions[0]?.img : selectedOption?.img"
                  :src="props.multiple ? selectedOptions[0]?.img : selectedOption?.img"
                  class="w-[20px] shrink-0"
                  alt="icon"
                  aria-hidden="true"
                >
                <component
                  :is="props.multiple ? selectedOptions[0]?.icon : selectedOption?.icon"
                  v-if="props.multiple ? selectedOptions[0]?.icon : selectedOption?.icon"
                  class="size-4 shrink-0"
                  aria-hidden="true"
                />
                {{
                  props.multiple
                    ? (selectedOptions[0]?.label ?? '') + (selectedOptions.length > 1 ? ' +' + (selectedOptions.length - 1) : '')
                    : selectedOption?.label
                }}
              </span>
              <span
                v-else
                class="text-muted-foreground"
              >{{ placeholder }}</span>
            </slot>
          </div>
          <ChevronsUpDown class="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        class="p-0"
        :style="{ width: 'var(--reka-popper-anchor-width, 18rem)' }"
      >
        <Command
          v-if="open"
          :disable-filter="remote"
        >
          <CommandSearchBridge
            v-if="remote"
            @search="emits('search', $event)"
          />
          <CommandInput :placeholder="searchPlaceholder" />
          <CommandEmpty>{{ loading ? 'Loading...' : emptyText }}</CommandEmpty>
          <CommandList>
            <CommandGroup>
              <CommandItem
                v-for="option in normalizedOptions"
                :key="option.value"
                :value="option.label"
                :disabled="option.disabled"
                @select="() => handleSelect(option.value)"
              >
                <Check
                  :class="cn(
                    'mr-2 h-4 w-4',
                    isSelected(option.value) ? 'opacity-100' : 'opacity-0'
                  )"
                />
                <slot
                  name="item"
                  :option="option.raw"
                >
                  <div class="flex items-center gap-2">
                    <img
                      v-if="option.img"
                      :src="option.img"
                      class="w-[20px]"
                      alt="icon"
                      aria-hidden="true"
                    >
                    <component
                      :is="option.icon"
                      v-if="option.icon"
                      class="size-4"
                      aria-hidden="true"
                    />
                    {{ option.label }}
                  </div>
                </slot>
              </CommandItem>
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  </div>
</template>
